import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { Search, FileSearch, AlertTriangle, ExternalLink, Download } from "lucide-react";

export default function Verify() {
  const [params, setParams] = useSearchParams();
  const [batch, setBatch] = useState(params.get("batch") || "");
  const [sku, setSku] = useState(params.get("sku") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = async (b, s) => {
    if (!b && !s) return;
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get("/coa/verify", { params: { batch: b || undefined, sku: s || undefined } });
      setResult(data);
    } catch { setResult([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get("batch") || params.get("sku")) {
      runSearch(params.get("batch") || "", params.get("sku") || "");
    }
    // eslint-disable-next-line
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ ...(batch && { batch }), ...(sku && { sku }) });
    runSearch(batch, sku);
  };

  return (
    <div>
      <section className="bg-[var(--paper-2)] border-b border-[var(--line)] relative overflow-hidden">
        <div className="absolute inset-0 starfield opacity-50" />
        <div className="nebula-blob cyan absolute" style={{ width: 380, height: 380, top: '-40%', right: '10%', opacity: 0.15 }} />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--terra)]">Public verification</div>
          <h1 className="h-display text-4xl md:text-5xl mt-2 leading-[1.04]">Verify a <span className="gradient-text">batch.</span></h1>
          <p className="mt-3 max-w-2xl text-[var(--ink-soft)]">Verify the authenticity and current status of any Nebula Aminos batch. Recalled or expired batches are clearly indicated. No account required.</p>
          <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 max-w-3xl" data-testid="verify-form">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--terra)]" />
              <input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="Batch # or prefix (e.g. NA, NA#-097)" className="w-full pl-10 pr-3 py-3 bg-[var(--paper-3)] text-[var(--ink)] font-mono text-sm rounded-full outline-none border border-[var(--line)] focus:border-[var(--terra)]" data-testid="verify-batch-input" />
            </div>
            <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="…or SKU" className="px-4 py-3 bg-[var(--paper-3)] text-[var(--ink)] font-mono text-sm rounded-full outline-none border border-[var(--line)] focus:border-[var(--terra)]" data-testid="verify-sku-input" />
            <button type="submit" className="btn-primary justify-center" data-testid="verify-submit">
              <FileSearch className="h-4 w-4" /> Verify
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-10">
        {loading && <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink-muted)]">Searching…</div>}
        {searched && !loading && result && result.length === 0 && (
          <div className="border border-[var(--crimson)] bg-red-50 rounded-md p-5 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[var(--crimson)] shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg text-[var(--crimson)] font-medium">No matching batch found.</div>
              <p className="text-sm text-[var(--crimson)]/80 mt-1">If your container shows this batch, do not use the material. Contact compliance.</p>
            </div>
          </div>
        )}
        {result && result.length > 0 && (
          <div className="space-y-4">
            {result.map((c) => (
              <div key={c.batch_number} className="border border-[var(--line)] bg-[var(--paper-2)] rounded-md overflow-hidden" data-testid={`verify-result-${c.batch_number}`}>
                <div className="px-5 py-4 border-b border-[var(--line)] flex flex-wrap items-center justify-between gap-3 bg-[var(--paper-3)]">
                  <div>
                    <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">Batch · {c.product?.sku}</div>
                    <div className="font-display text-2xl font-medium mt-1 text-[var(--terra)] glow-cyan">{c.batch_number}</div>
                    <div className="text-sm mt-1 text-[var(--ink-soft)]">{c.product?.product_name}</div>
                  </div>
                  <ComplianceBadge kind="coa" value={c.status} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--line)] border-b border-[var(--line)]">
                  {[["Purity", `${c.purity_percent}%`], ["Test Date", c.test_date], ["Retest", c.retest_date || "—"], ["Lab", c.testing_lab]].map(([k, v]) => (
                    <div key={k} className="px-5 py-4">
                      <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">{k}</div>
                      <div className="font-mono text-sm mt-1">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs font-mono text-[var(--ink-muted)]">Methods: {(c.testing_methods || []).join(" · ")}</div>
                  {c.external_verify_url ? (
                    <a href={c.external_verify_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs" data-testid={`verify-batch-${c.batch_number}`}>
                      <ExternalLink className="h-4 w-4" /> Verify on Koveralabs
                    </a>
                  ) : c.coa_pdf_url ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${c.coa_pdf_url}`} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs">
                      <Download className="h-4 w-4" /> Download PDF
                    </a>
                  ) : c.testing_lab === "Koveralabs" ? (
                    <a href="https://koveralabs.com" target="_blank" rel="noopener noreferrer" className="btn-outline text-xs">
                      <ExternalLink className="h-4 w-4" /> Verify on Koveralabs
                    </a>
                  ) : (
                    <span className="font-mono text-xs text-[var(--ink-muted)]">PDF pending</span>
                  )}
                </div>
                {c.status === "recalled" && (
                  <div className="border-t border-[var(--crimson)] bg-red-50 px-5 py-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-[var(--crimson)] shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-display text-lg font-medium text-[var(--crimson)]">Batch recalled</div>
                      <div className="text-[var(--crimson)]/80 text-xs mt-1">{c.recall_reason || "This batch has been withdrawn. Discontinue analytical use immediately."}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!searched && (
          <div className="text-center py-16">
            <div className="font-display text-xl text-[var(--ink-muted)]">Search a batch number above to begin.</div>
            <Link to="/coa" className="btn-pill inline-flex mt-4">Browse all batches →</Link>
          </div>
        )}
      </section>
    </div>
  );
}
